import { Timestamp, collection, doc, writeBatch } from "firebase/firestore";
import { Timestamp as LiteTimestamp, where as liteWhere, type QueryConstraint } from "firebase/firestore/lite";
import { BetDetails } from "../../models/MyBetModel.tsx";
import { filterBetsByFiguresDateRange, widenBetQueryDateRange, widenBetGameDateQueryRange, mergeBetQueryDocs, MULTI_LEG_BET_TYPES } from "../../helpers/functions.tsx";
import { Schedule } from "../../models/Schedule.tsx";
import { oddsjamApi } from "./apis/oddsjamApi.tsx";
import { Transaction } from "../../models/User.tsx";
import db from "../Firebase.js";
import { getLiteQueryDocs } from "../../helpers/firestoreLiteQuery.ts";

// export async function placeAllWagers(bets: BetDetails[]): Promise<void> {
//     const batch = writeBatch(db);
//     bets.forEach(b => batch.set(doc(collection(db, 'myBet')), { ...b, odds: [] }));
//     batch.commit();
// }

export async function placeAllWagers(bets: BetDetails[]): Promise<void> {
    const batch = writeBatch(db);

    bets.forEach(b => {
        const cleanedSelections = b.selections ? b.selections.map(({ odds, ...rest }) => rest) : [];
        const { odds, selections, ...restBet } = b;
        batch.set(doc(collection(db, 'myBet')), {
            ...restBet,
            selections: cleanedSelections
        });
    });

    await batch.commit();
}

export async function getUserBets(userName: string, dateFrom: Timestamp, dateTo: Timestamp) {
    const docs = await getLiteQueryDocs('myBet', [
        liteWhere('player.userName', '==', userName)
    ]);
    const bets: any[] = [];

    docs.forEach(docSnap => {
        const data = docSnap.data() as BetDetails;

        if (data.status === 'deleted') return;
        if (!data.betDate || data.betDate < dateFrom || data.betDate > dateTo) return;

        if ((data.betType === 'Parlay' || data.betType === 'Teaser') && Array.isArray(data.selections)) {
            bets.push({ ...data });
        } else {
            bets.push(data);
        }

    });
    return bets;
}

export async function getBetsByStatus(userName: string) {
    const docs = await getLiteQueryDocs('myBet', [
        liteWhere('player.userName', '==', userName),
        liteWhere("betStatus", "in", [
            "pending",
            "GradingPending"
        ]),
    ]);
    const bets: any[] = [];

    docs.forEach(docSnap => {
        const data = docSnap.data();

            bets.push(data as BetDetails);
        
    });
    return bets;
}

export async function betResult(id: string) {
    const gamesRequest = await oddsjamApi.get('/schedules', { params: { id } });
    return gamesRequest.data['data'] as Schedule[];
}

const generateRandom9DigitNumber = () => {
    return Math.floor(100000000 + Math.random() * 900000000);
};

export const isUniqueTicket = async (ticket: number): Promise<boolean> => {
    const docs = await getLiteQueryDocs("myBet", [
        liteWhere("ticket", "==", ticket),
    ]);
    return docs.length === 0;
};

export const generateUniqueTicket = async (): Promise<number> => {
    const MAX_ATTEMPTS = 4;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        const newTicket = generateRandom9DigitNumber();
        if (await isUniqueTicket(newTicket)) {
            return newTicket;
        }
    }

    let fallback = Date.now() % 1_000_000_000;
    if (fallback < 100_000_000) fallback += 100_000_000;
    return fallback;
};


export async function getBetsBydate(userName: string, dateFrom: Date | Timestamp, dateTo: Date | Timestamp) {
    const rangeStart = dateFrom instanceof Date ? dateFrom : dateFrom.toDate();
    const rangeEnd = dateTo instanceof Date ? dateTo : dateTo.toDate();
    const { queryStart, queryEnd } = widenBetQueryDateRange(rangeStart, rangeEnd);
    const { queryStart: gameQueryStart, queryEnd: gameQueryEnd } = widenBetGameDateQueryRange(rangeStart, rangeEnd);

    const baseConstraints = [
        liteWhere('player.userName', '==', userName),
        liteWhere("betStatus", "in", ["completed", "GradedWithTransaction"]),
    ];

    // Straight y demás: solo lastUpdateBySystem. Parlay/Teaser: también gameDate (legacy).
    const [byLastUpdate, byMultiLegGameDate] = await Promise.all([
        getLiteQueryDocs("myBet", [
            ...baseConstraints,
            liteWhere('lastUpdateBySystem', '>=', LiteTimestamp.fromDate(queryStart)),
            liteWhere('lastUpdateBySystem', '<=', LiteTimestamp.fromDate(queryEnd)),
        ]),
        getLiteQueryDocs("myBet", [
            ...baseConstraints,
            liteWhere('betType', 'in', [...MULTI_LEG_BET_TYPES]),
            liteWhere('gameDate', '>=', LiteTimestamp.fromDate(gameQueryStart)),
            liteWhere('gameDate', '<=', LiteTimestamp.fromDate(gameQueryEnd)),
        ]),
    ]);

    const bets: BetDetails[] = [];
    mergeBetQueryDocs({ docs: byLastUpdate }, { docs: byMultiLegGameDate }).forEach(docSnap => {
        bets.push(docSnap.data() as BetDetails);
    });

    return filterBetsByFiguresDateRange(bets, rangeStart, rangeEnd);
}

export async function betTransaction(
    userName: string,
    dateFrom: Date | Timestamp,
    dateTo: Date | Timestamp
): Promise<Transaction[]> {
    const start = dateFrom instanceof Date ? LiteTimestamp.fromDate(dateFrom) : LiteTimestamp.fromMillis(dateFrom.toMillis());
    const end = dateTo instanceof Date ? LiteTimestamp.fromDate(dateTo) : LiteTimestamp.fromMillis(dateTo.toMillis());

    const docs = await getLiteQueryDocs("transaction", [
        liteWhere("customer", "==", userName),
        liteWhere("createDate", ">=", start),
        liteWhere("createDate", "<=", end),
    ]);

    const transactions = docs.map((docSnap) => {
        const data = docSnap.data() as Omit<Transaction, "id">;
        return {
            id: docSnap.id,
            ...data,
            createDate: data.createDate as Timestamp,
        };
    });

    return transactions.filter(
        (tx) => tx.status !== "delete" && tx.transactionFree !== true
    );
}

/** Bet type for the tickets already loaded in a week. Does not scan by date. */
export async function getBetTypesByTickets(tickets: Array<string | number | null | undefined>): Promise<Record<string, string>> {
    const strings = new Set<string>();
    const numbers = new Set<number>();

    tickets.forEach((raw) => {
        const text = raw == null ? "" : String(raw).trim();
        if (!text) return;
        strings.add(text);
        if (/^\d+$/.test(text)) numbers.add(Number(text));
    });

    const types: Record<string, string> = {};

    const readChunk = async (values: Array<string | number>) => {
        if (values.length === 0) return;
        const docs = await getLiteQueryDocs("myBet", [
            liteWhere("ticket", "in", values),
        ]);
        docs.forEach((docSnap) => {
            const data = docSnap.data() as BetDetails;
            const ticket = data.ticket != null ? String(data.ticket).trim() : "";
            if (ticket && data.betType) types[ticket] = data.betType;
        });
    };

    const chunksOf = (values: Array<string | number>) => {
        const chunks: Array<Array<string | number>> = [];
        for (let i = 0; i < values.length; i += 30) chunks.push(values.slice(i, i + 30));
        return chunks;
    };

    await Promise.all(chunksOf([...strings]).map(readChunk));
    const missingNumbers = [...numbers].filter((value) => !types[String(value)]);
    await Promise.all(chunksOf(missingNumbers).map(readChunk));

    return types;
}

// export async function getUserBetsByCategory(
//     userName: string,
//     betType: string,
//     gameType?: string,
//     fixture_id?: string
// ): Promise<number> {

//     const collectionRef = collection(db, "myBet");

//     let constraints: any[] = [
//         where("player.userName", "==", userName),
//         where("betStatus", "==", "pending"),
//         where("betType", "==", betType),
//          where("fixtureId", "==", fixture_id)
//     ];

//     if (gameType) {
//         constraints.push(where("gameType", "==", gameType));
//     }

//     const betsQuery = query(collectionRef, ...constraints);
//     const snapshot = await getDocs(betsQuery);

//     let total = 0;

//     snapshot.forEach(doc => {
//         total += Number(doc.data().bet ?? 0);
//     });

//     console.log(total)

//     return total; 
// }

const chunkArray = <T,>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

async function safePendingQuery(
    constraints: QueryConstraint[]
): Promise<BetDetails[]> {
    try {
        const docs = await getLiteQueryDocs("myBet", constraints);
        return docs.map(docSnap => docSnap.data() as BetDetails);
    } catch (error) {
        console.error("Pending bets query failed:", error);
        return [];
    }
}

export async function getPendingBetsForPlacement(
    userName: string,
    bets: BetDetails[],
    options?: { includeParlays?: boolean; includeTeasers?: boolean }
): Promise<BetDetails[]> {
    const baseConstraints = [
        liteWhere("player.userName", "==", userName),
        liteWhere("betStatus", "==", "pending"),
    ];

    const fixtureIds = [...new Set(bets.map(b => b.fixture_id).filter(Boolean))] as string[];
    const needsContest = bets.some(b => b.betType === "Contest");
    const includeParlays = options?.includeParlays === true;
    const includeTeasers = options?.includeTeasers === true;

    if (fixtureIds.length === 0 && !needsContest && !includeParlays && !includeTeasers) {
        return [];
    }

    const queryPromises: Promise<BetDetails[]>[] = [];

    for (const chunk of chunkArray(fixtureIds, 10)) {
        queryPromises.push(
            safePendingQuery([...baseConstraints, liteWhere("fixture_id", "in", chunk)])
        );
    }

    if (needsContest) {
        queryPromises.push(
            safePendingQuery([...baseConstraints, liteWhere("betType", "==", "Contest")])
        );
    }

    // Parlays/Teasers have no top-level fixture_id; reuse the pending query that already works in UI.
    if (includeParlays || includeTeasers) {
        queryPromises.push(
            getUserPendingBets(userName).then(rows =>
                rows
                    .filter(b => {
                        const type = String((b as BetDetails).betType ?? "").toLowerCase();
                        return (
                            (includeParlays && type === "parlay") ||
                            (includeTeasers && type === "teaser")
                        );
                    })
                    .map(b => b as BetDetails)
            )
        );
    }

    const results = await Promise.all(queryPromises);
    const seen = new Set<string>();
    const merged: BetDetails[] = [];

    for (const group of results) {
        for (const bet of group) {
            const key = `${bet.ticket ?? ""}:${bet.gameId ?? ""}:${bet.fixture_id ?? ""}:${bet.betType ?? ""}`;
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(bet);
        }
    }

    return merged;
}

export async function getUserPendingBets(userName: string) {
    const docs = await getLiteQueryDocs("myBet", [
        liteWhere("player.userName", "==", userName),
        liteWhere("betStatus", "==", "pending"),
    ]);
    return docs.map(docSnap => docSnap.data());
}